// Única fuente de verdad de la forma de calculatorConfig por tipo.
// Sin React: importable desde tests puros y desde PlanesForm.

export const CALCULATOR_TYPE_VALUES = [
  "tramo_edad",
  "monto_tasa",
  "construccion_comuna",
  "precio_base_atributos",
  "fijo",
  "por_mascota",
  "precio_cantidad",
];

const CARGAS_SUPPORTED = new Set([
  "tramo_edad",
  "monto_tasa",
  "construccion_comuna",
  "precio_base_atributos",
]);

export function supportsCargas(type) {
  return CARGAS_SUPPORTED.has(type);
}

const DEFAULT_DESCUENTOS_CARGAS = {
  tabla: "descuentos_base",
  aplicacion: "descuento_sobre_titular",
  parametros: { plan_id: "eq", tipo_carga: "eq" },
  campoResultado: "porcentaje",
};

const DEFAULT_CARGAS_PRICING = {
  mode: "absolute",
  tabla: "precios_carga",
  parametros: { tipo_carga: "eq", precio_base_id: "eq" },
  campoResultado: "precio_uf",
};

function baseCargas() {
  return {
    cargasModel: "descuentosCargas",
    descuentosCargas: { ...DEFAULT_DESCUENTOS_CARGAS, parametros: { ...DEFAULT_DESCUENTOS_CARGAS.parametros } },
  };
}

// --- Validación externa por RUT (gate de elegibilidad) -----------------------
//
// Vive en `calculatorConfig.validacionExterna` y es del PLAN, no del tipo de
// cálculo: por eso no está en `defaultsFor` y sobrevive al cambio de tipo.
//
// Cuando está presente, flow exige el RUT en el miniform y su `/api/calculate`
// no entrega precios sin validarlo (el gate es server-side, no evadible).
// Ausente = no se pide RUT para cotizar. Lo que el cliente ingresa al contratar
// es otra cosa y no depende de esto.
export const VALIDACION_SISTEMAS = ["SISGEN", "compania"];

export const VALIDACION_EXTERNA_DEFAULT = {
  tipo: "rut_elegibilidad",
  campo: "rut",
  sistemas: [...VALIDACION_SISTEMAS],
  mensajeBloqueo: {
    titulo: "Por ahora no podemos entregarte precios para este RUT.",
    detalle:
      "Según nuestros sistemas de evaluación, este RUT no puede cotizar en línea en este momento. Si crees que es un error, contáctanos y lo revisamos.",
  },
};

// Objeto nuevo en cada llamada: el toggle del formulario lo mete en el estado y
// después se edita por path, así que compartir la constante la mutaría.
export function nuevaValidacionExterna() {
  return {
    ...VALIDACION_EXTERNA_DEFAULT,
    sistemas: [...VALIDACION_EXTERNA_DEFAULT.sistemas],
    mensajeBloqueo: { ...VALIDACION_EXTERNA_DEFAULT.mensajeBloqueo },
  };
}

export function defaultsFor(type) {
  switch (type) {
    case "tramo_edad":
      return {
        type,
        precioBase: { tabla: "precios_base", parametros: { edad: "rango", plan_id: "eq" }, campoResultado: "precio_uf" },
        factores: { actividad: { activo: false, tabla: "actividades", campo: "factor_riesgo" } },
        ...baseCargas(),
      };
    case "monto_tasa":
      return {
        type,
        tasa: { tabla: "precios_base", parametros: { plan_id: "eq", Activo: "eq" }, campoResultado: "tasa" },
        montoAsegurado: { origen: "plan", campo: "capital_uf" },
        ...baseCargas(),
      };
    case "construccion_comuna":
    case "precio_base_atributos":
      return {
        type,
        precioBase: { tabla: "precios_base", parametros: { plan_id: "eq", Activo: "eq" }, atributosParametros: {}, campoResultado: "precio_uf" },
        ...baseCargas(),
      };
    case "fijo":
    case "por_mascota":
      return {
        type,
        precioBase: { tabla: "precios_base", parametros: { plan_id: "eq", Activo: "eq" }, campoResultado: "precio_uf" },
      };
    case "precio_cantidad":
      return {
        type,
        precioBase: { tabla: "precios_base", parametros: { plan_id: "eq", Activo: "eq" }, campoResultado: "precio_uf" },
        // `atributo` y `maximo` repiten los defaults del motor (PrecioCantidadCalculator)
        // y del miniform de flow (MAX_CANTIDAD_FALLBACK): un plan recién creado se
        // comporta igual con el bloque presente o ausente.
        cantidad: { atributo: "cantidad", maximo: 99 },
      };
    default:
      return { type };
  }
}

function isObject(v) {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function deepMerge(base, over) {
  if (!isObject(base)) return over;
  if (!isObject(over)) return over === undefined ? base : over;
  const out = { ...base };
  for (const key of Object.keys(over)) {
    out[key] = over[key];
  }
  return out;
}

export function parse(rawCalculatorConfig) {
  const raw = isObject(rawCalculatorConfig) ? rawCalculatorConfig : {};
  const type = CALCULATOR_TYPE_VALUES.includes(raw.type) ? raw.type : "tramo_edad";
  const merged = deepMerge(defaultsFor(type), { ...raw, type });
  if (supportsCargas(type)) {
    merged.cargasModel = raw.cargasPricing
      ? "cargasPricing"
      : raw.descuentosCargas
        ? "descuentosCargas"
        : merged.cargasModel || "descuentosCargas";
    if (merged.cargasModel === "cargasPricing" && !merged.cargasPricing) {
      merged.cargasPricing = { ...DEFAULT_CARGAS_PRICING, parametros: { ...DEFAULT_CARGAS_PRICING.parametros } };
    }
  }
  return merged;
}

// `cantidad` (solo precio_cantidad) es lo único que el cliente ingresa en p × q.
// Se emite normalizado —entero para `maximo`, strings sin espacios sobrantes, sin
// claves vacías— porque el motor y el miniform aplican sus defaults cuando la clave
// falta, y un "" o un NaN guardado los pisaría con un valor peor que la ausencia.
function serializeCantidad(cantidad) {
  const c = isObject(cantidad) ? cantidad : {};
  const out = {};

  const atributo = typeof c.atributo === "string" ? c.atributo.trim() : "";
  if (atributo) out.atributo = atributo;

  const maximo = Number(c.maximo);
  if (Number.isInteger(maximo) && maximo >= 1) out.maximo = maximo;

  const etiquetas = {};
  for (const clave of ["singular", "plural", "pregunta"]) {
    const valor = typeof c.etiquetas?.[clave] === "string" ? c.etiquetas[clave].trim() : "";
    if (valor) etiquetas[clave] = valor;
  }
  if (Object.keys(etiquetas).length > 0) out.etiquetas = etiquetas;

  return out;
}

// `validacionExterna` se emite normalizada: sistemas acotados a los que flow
// sabe consultar y mensaje de bloqueo siempre con texto. Un sistema inventado
// haría que el resolver lance ProviderUnavailableError y flow responda 502 a
// TODA cotización del plan, no solo a la del RUT bloqueado.
//
// `tipo` y `campo` se conservan si vienen: hoy solo existe rut_elegibilidad/rut,
// pero pisarlos sería borrar algo que no puso este formulario.
function serializeValidacionExterna(v) {
  if (!isObject(v)) return null;
  const sistemas = Array.isArray(v.sistemas)
    ? v.sistemas.filter((s) => VALIDACION_SISTEMAS.includes(s))
    : [];
  const titulo = typeof v.mensajeBloqueo?.titulo === "string" ? v.mensajeBloqueo.titulo.trim() : "";
  const detalle = typeof v.mensajeBloqueo?.detalle === "string" ? v.mensajeBloqueo.detalle.trim() : "";
  return {
    tipo: typeof v.tipo === "string" && v.tipo ? v.tipo : VALIDACION_EXTERNA_DEFAULT.tipo,
    campo: typeof v.campo === "string" && v.campo ? v.campo : VALIDACION_EXTERNA_DEFAULT.campo,
    sistemas,
    mensajeBloqueo: {
      titulo: titulo || VALIDACION_EXTERNA_DEFAULT.mensajeBloqueo.titulo,
      detalle: detalle || VALIDACION_EXTERNA_DEFAULT.mensajeBloqueo.detalle,
    },
  };
}

export function serialize(calculatorConfig) {
  const cc = isObject(calculatorConfig) ? calculatorConfig : {};
  const type = CALCULATOR_TYPE_VALUES.includes(cc.type) ? cc.type : "tramo_edad";
  const out = { type };

  if (type === "tramo_edad") {
    if (cc.precioBase) out.precioBase = cc.precioBase;
    if (cc.factores) out.factores = cc.factores;
  } else if (type === "monto_tasa") {
    if (cc.tasa) out.tasa = cc.tasa;
    if (cc.montoAsegurado) out.montoAsegurado = cc.montoAsegurado;
  } else {
    // construccion_comuna, precio_base_atributos, fijo, por_mascota, precio_cantidad
    if (cc.precioBase) out.precioBase = cc.precioBase;
    if (type === "precio_cantidad") {
      const cantidad = serializeCantidad(cc.cantidad);
      if (Object.keys(cantidad).length > 0) out.cantidad = cantidad;
    }
  }

  if (supportsCargas(type)) {
    const model = cc.cargasModel || (cc.cargasPricing ? "cargasPricing" : "descuentosCargas");
    if (model === "cargasPricing" && cc.cargasPricing) out.cargasPricing = cc.cargasPricing;
    else if (cc.descuentosCargas) out.descuentosCargas = cc.descuentosCargas;
  }
  if (typeof cc.miniform === "string" && cc.miniform) out.miniform = cc.miniform;
  const validacionExterna = serializeValidacionExterna(cc.validacionExterna);
  if (validacionExterna) out.validacionExterna = validacionExterna;
  return out;
}

export function mergeOnTypeChange(prev, newType) {
  const next = defaultsFor(newType);
  if (isObject(prev) && supportsCargas(newType) && supportsCargas(prev.type)) {
    if (prev.cargasModel) next.cargasModel = prev.cargasModel;
    if (prev.descuentosCargas) next.descuentosCargas = prev.descuentosCargas;
    if (prev.cargasPricing) next.cargasPricing = prev.cargasPricing;
  }
  // La asignación de miniform es del plan, no del tipo de cálculo: sobrevive al cambio.
  if (isObject(prev) && typeof prev.miniform === "string" && prev.miniform) {
    next.miniform = prev.miniform;
  }
  // Idem el gate de RUT: exigirlo o no es una decisión del plan, y cambiar de
  // tipo de cálculo no la revoca.
  if (isObject(prev) && isObject(prev.validacionExterna)) {
    next.validacionExterna = prev.validacionExterna;
  }
  return next;
}
